import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { merge } from 'lodash-es';
import { FUSE_APP_CONFIG } from '@fuse/services/config/config.constants';
import { environment } from 'environments/environment';
import { AUTH_STORAGE_KEYS, AuthStorage, AuthStorageKey } from 'app/session/auth/auth.storage';

type LayoutBrandingChanges = {
    theme?: string;
    logo?: string;
    watermark?: string;
    favicon?: string;
};

@Injectable({
    providedIn: 'root'
})
export class FuseConfigService {
    private _config: BehaviorSubject<any>;
    private readonly _fallbackLayout: Required<LayoutBrandingChanges> = {
        logo: 'https://cloud.ashcorp.co/index.php/s/fX7kZWEkEysJrqb/download/logo-20260328151452-e9f33243.png',
        theme: 'default',
        watermark: 'https://cloud.ashcorp.co/index.php/s/Btp2mpragCTSniC/download/watermark-20260328151320-0bcf126c.png',
        favicon: 'https://cloud.ashcorp.co/index.php/s/MwMofkqidT9pYPZ/download/favicon-20260328151518-4af7f78e.png'
    };

    /**
     * Constructor
     */
    constructor(@Inject(FUSE_APP_CONFIG) config: any) {
        // Private
        this._config = new BehaviorSubject(config);

        const runtimeLayout = environment.layout;
        const hostLayout = this._getHostLayout();
        const activeLayout = this._getActiveLayout();
        const theme = //localStorage.getItem(AUTH_STORAGE_KEYS.hostLayoutThemeTmp)
            activeLayout.theme
            || hostLayout.theme
            || runtimeLayout.theme
            || this._fallbackLayout.theme;

        this.config = {
            "logo": activeLayout.logo || hostLayout.logo || runtimeLayout.logo || this._fallbackLayout.logo,
            "theme": theme,
            "watermark": activeLayout.watermark || hostLayout.watermark || runtimeLayout.watermark || this._fallbackLayout.watermark,
            "favicon": activeLayout.favicon || hostLayout.favicon || runtimeLayout.favicon || this._fallbackLayout.favicon
        };
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for config
     */
    set config(value: any) {
        // Merge the new config over to the current config
        const config = merge({}, this._config.getValue(), value);
        // this._setStorageValue(AUTH_STORAGE_KEYS.hostLayoutThemeTmp, config.theme);
        this._setFavicon(config.favicon);

        // Execute the observable
        this._config.next(config);
    }

    get config$(): Observable<any> {
        return this._config.asObservable();
    }

    get config(): any {
        return this._config.getValue();
    }

    /**
     * Getter pour le thème actuel
     */
    get theme(): string {
        return this._config.getValue().theme ?? "default";
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Resets the config to the default
     */
    reset(): void {
        this._config.next(this.config);
    }

    applyPermanentLayoutChanges(value: LayoutBrandingChanges): void {
        const config = merge({}, this._config.getValue(), value);
        this._setFavicon(config.favicon);
        this._config.next(config);
    }

    private _getActiveLayout(): Required<LayoutBrandingChanges> {
        const hostLayout = this._getHostLayout();
        const runtimeLayout = environment.layout;

        return {
            logo: hostLayout.logo || runtimeLayout.logo || this._fallbackLayout.logo,
            theme: hostLayout.theme || runtimeLayout.theme || this._fallbackLayout.theme,
            watermark: hostLayout.watermark || runtimeLayout.watermark || this._fallbackLayout.watermark,
            favicon: hostLayout.favicon || runtimeLayout.favicon || this._fallbackLayout.favicon
        };
    }

    private _getHostLayout(): Required<LayoutBrandingChanges> {
        const runtimeLayout = environment.layout;

        return {
            logo: this._readStorageValue(AUTH_STORAGE_KEYS.hostLogo) || runtimeLayout.logo || this._fallbackLayout.logo,
            theme: this._readStorageValue(AUTH_STORAGE_KEYS.hostTheme) || runtimeLayout.theme || this._fallbackLayout.theme,
            watermark: this._readStorageValue(AUTH_STORAGE_KEYS.hostWatermark) || runtimeLayout.watermark || this._fallbackLayout.watermark,
            favicon: this._readStorageValue(AUTH_STORAGE_KEYS.hostFavicon) || runtimeLayout.favicon || this._fallbackLayout.favicon
        };
    }

    private _readStorageValue(key: AuthStorageKey): string {
        return `${AuthStorage.get(key) || ''}`.trim();
    }

    private _setFavicon(url: string): void {
        if (!url || typeof document === 'undefined') {
            return;
        }

        const favicon = document.getElementById('app-favicon');
        if (favicon) {
            favicon.setAttribute('href', url);
        }
    }
}
